import { forwardRef, useEffect } from 'react';

// material-ui
import {
    Button,
    Dialog,
    FormControl,
    FormHelperText,
    IconButton,
    Grid,
    Stack,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Zoom,
    DialogContent
} from '@mui/material';

// project imports
import MainCard from 'ui-component/cards/MainCard';
import { gridSpacing } from 'store/constant';
import validationSchema from 'utils/schema/TurfSchema';
import useApi from 'hooks/useApi';
import VenueService from 'services/VenueService';

// assets
import HighlightOffTwoToneIcon from '@mui/icons-material/HighlightOffTwoTone';

// third-party
import { useFormik } from 'formik';

const Transition = forwardRef((props, ref) => <Zoom ref={ref} {...props} />);

const AddTurf = ({ selectedTurf, open, handleClose, venueUuid, setReload, venueDetails, sportsTypes, setSportsType }) => {

    const submitHandler = () => {
        const formikValues = { ...formik.values };
        formikValues.sports_type = formikValues.sports_type.join(',');
        formikValues.uuid_venue = venueUuid;
        if (selectedTurf) {
            updateTurfByUuidApi.execute(formikValues, selectedTurf.uuid);
        } else {
            createTurfApi.execute(formikValues);
        }
    };

    const closeDialog = () => {
        formik.resetForm();
        handleClose();
    };

    const createSuccessHandler = () => {
        formik.setSubmitting(true);
        formik.resetForm();
        setReload(r => !r);
        handleClose();
    };

    const createTurfApi = useApi(VenueService.createTurf, createSuccessHandler, true);
    const updateTurfByUuidApi = useApi(VenueService.updateTurfByUuid, createSuccessHandler, true);

    const formik = useFormik({
        initialValues: {
            turf_name: selectedTurf ? selectedTurf.turf_name : '',
            sports_type: selectedTurf ? selectedTurf.sports_type.split(',') : []
        },
        enableReinitialize: true,
        validationSchema: validationSchema,
        onSubmit: submitHandler
    });

    useEffect(() => {
        if (selectedTurf) {
            formik.setFieldValue('turf_name', selectedTurf.turf_name);
            formik.setFieldValue('sports_type', selectedTurf.sports_type.split(','));
        }
    }, [selectedTurf]);

    useEffect(() => {
        if (venueDetails) {
            let sts = [];
            venueDetails.sports_type.split(',').forEach((st) => {
                let findSt = sportsTypes.find(x => x.code === st);
                if (findSt) {
                    sts.push(findSt);
                }
            });
            setSportsType(sts);
        }
    }, [venueDetails]);

    return (
        <Dialog
            open={open}
            TransitionComponent={Transition}
            keepMounted
            onClose={closeDialog}
            aria-labelledby="alert-dialog-slide-title"
            aria-describedby="alert-dialog-slide-description"
        >
            <DialogContent sx={{ p: 0 }}>
                <MainCard
                    title={selectedTurf ? 'Edit Court Details' : 'Add Court Details'}
                    secondary={
                        <IconButton onClick={closeDialog} size="large" aria-label="Close Court">
                            <HighlightOffTwoToneIcon fontSize="small" />
                        </IconButton>
                    }
                    sx={{ border: 0 }}
                >

                    <Grid container spacing={gridSpacing}>
                        <Grid item xs={12}>
                            <FormControl fullWidth size="medium">
                                <TextField
                                    fullWidth
                                    name="turf_name"
                                    label="Court Name*"
                                    error={Boolean(formik.touched.turf_name && formik.errors.turf_name)}
                                    value={formik.values.turf_name}
                                    onBlur={formik.handleBlur}
                                    onChange={formik.handleChange}
                                />
                            </FormControl>
                            {formik.touched.turf_name && formik.errors.turf_name && (
                                <FormHelperText error id="standard-weight-helper-text-turf-name-venue">
                                    {formik.errors.turf_name}
                                </FormHelperText>
                            )}
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth size="medium">
                                <InputLabel id="sport-type-select-label">Sports Type*</InputLabel>
                                <Select
                                    labelId="sport-type-select-label"
                                    id="sport-type-select"
                                    label="Sport Type*"
                                    name="sports_type"
                                    value={formik.values.sports_type}
                                    multiple
                                    error={Boolean(formik.touched.sports_type && formik.errors.sports_type)}
                                    onBlur={formik.handleBlur}
                                    onChange={formik.handleChange}
                                >
                                    {
                                        sportsTypes.map((sportType, index) => {
                                            return (
                                                <MenuItem key={index} value={sportType.code}>{sportType.display_name}</MenuItem>
                                            )
                                        })
                                    }
                                </Select>
                            </FormControl>
                            {formik.touched.sports_type && formik.errors.sports_type && (
                                <FormHelperText error id="standard-weight-helper-text-sportstype-venue">
                                    {formik.errors.sports_type}
                                </FormHelperText>
                            )}
                        </Grid>
                        <Grid item xs={12}>
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                                <Button
                                    color="error"
                                    onClick={closeDialog}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    color="secondary"
                                    variant="contained"
                                    onClick={formik.handleSubmit}
                                >
                                    {selectedTurf ? 'Update' : 'Create'} Court
                                </Button>
                            </Stack>
                        </Grid>
                    </Grid>
                </MainCard>
            </DialogContent>
        </Dialog>
    );
};

export default AddTurf;
